import type { ContentType, PlaylistItem } from '../../types';
import type { CompareAwarePlayerOptions } from '../compare_aware_player/compare_aware_player';
import { MediaPlayer } from './media_player';
import { WaveformCanvas } from './waveform_canvas';
import { showWaveformUnavailableMessage } from './waveform_loader';

const TIME_SETTLED_EVENTS = ['seeked', 'pause', 'ended'] as const;

export class AudioPlayer extends MediaPlayer {
  declare mediaElement: HTMLAudioElement | undefined;

  playerType: ContentType = 'audio';

  #animationRequestId: number | undefined;

  #waveFormCanvas: WaveformCanvas | undefined;
  #unavailableMessage: HTMLElement | undefined;

  constructor(options: CompareAwarePlayerOptions) {
    super(options);

    const mediaElement = document.createElement('audio');
    mediaElement.crossOrigin = 'anonymous';
    for (const eventName of TIME_SETTLED_EVENTS) {
      mediaElement.addEventListener(eventName, this.#handleTimeSettled);
    }
    this.mediaElement = mediaElement;

    this.#animationRequestId = requestAnimationFrame(
      this.#handleAnimationFrame,
    );

    this.subscribersList.subscribe(
      this.store.playableMedia.$shouldBePlaying,
      this.#handleShouldBePlaying,
    );
  }

  async destroy() {
    if (this.mediaElement) {
      for (const eventName of TIME_SETTLED_EVENTS) {
        this.mediaElement.removeEventListener(
          eventName,
          this.#handleTimeSettled,
        );
      }
      this.mediaElement.src = '';
      this.mediaElement.load();
    }
    await super.destroy();

    if (this.#animationRequestId) {
      cancelAnimationFrame(this.#animationRequestId);
      this.#animationRequestId = undefined;
    }

    this.#clearWaveform();
  }

  #clearWaveform() {
    this.#waveFormCanvas?.destroy();
    this.#waveFormCanvas = undefined;
    this.#unavailableMessage?.remove();
    this.#unavailableMessage = undefined;
  }

  async load(playlistItem: PlaylistItem): Promise<void> {
    if (!playlistItem.url) {
      return;
    }
    await super.load(playlistItem);
    if (!this.mediaElement) {
      return;
    }
    this.#clearWaveform();
    // The waveform spans the whole media, but a subclip's progress cursor and
    // click-to-seek are normalized to its playable range, so the two disagree.
    // Drawing it would also decode the entire parent file.
    // TODO(FE-3795): crop the waveform to the playable range instead.
    if (playlistItem.playableRange) {
      this.#unavailableMessage = showWaveformUnavailableMessage(
        this.containerElement,
      );
      return;
    }
    this.#waveFormCanvas = new WaveformCanvas(
      this.containerElement,
      this.store,
    );
    this.#waveFormCanvas.setUrl(this.mediaElement.src);
  }

  // The animation frame loop only runs while playing, so seeks made while
  // paused and the final position after pausing are picked up here
  #handleTimeSettled = () => {
    this.refreshStoreCurrentTime();
  };

  #handleAnimationFrame = () => {
    this.refreshStoreCurrentTime();
    this.#animationRequestId = requestAnimationFrame(
      this.#handleAnimationFrame,
    );
  };

  #handleShouldBePlaying = (shouldBePlaying: boolean) => {
    if (shouldBePlaying) {
      this.#animationRequestId = requestAnimationFrame(
        this.#handleAnimationFrame,
      );
    } else {
      if (this.#animationRequestId) {
        cancelAnimationFrame(this.#animationRequestId);
        this.#animationRequestId = undefined;
      }
    }
  };

  play() {
    super.play();
  }
}
